import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import {
  HeartPulse, Users, ShieldCheck, Wallet, Sparkles, RefreshCw, Bell, CheckSquare,
  ArrowRight, AlertTriangle, Loader2, SlidersHorizontal, Lock, Star, Eye, EyeOff, X, GripVertical,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ScoreRing, PriorityBadge, scoreColor } from "@/components/common/ui";
import InsightCard from "@/components/ai/InsightCard";
import { modulesForRole, modulesForIds, MODULES, recordRecentModule } from "@/lib/modules";
import { cn } from "@/lib/utils";

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}
const roleLabel = (r) => ({ admin: "System Administrator", executive: "Executive", program_manager: "Program Manager", nurse: "Registered Nurse", dsp: "Direct Support Professional", clinician: "Clinician", compliance: "Compliance Officer", technician: "Technician" }[r] || "Team Member");

export default function CommandCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const ctx = useOutletContext();
  const [data, setData] = useState(null);
  const [briefing, setBriefing] = useState("");
  const [briefingLoading, setBriefingLoading] = useState(true);
  const [prefs, setPrefs] = useState({ hidden: [], pinned: [], order: [], show_overview: true });
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [allowedIds, setAllowedIds] = useState(null);
  const [dragItem, setDragItem] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [security, setSecurity] = useState(null);
  const isAdmin = user?.role === "admin" || user?.role === "executive";

  const roleModules = allowedIds ? modulesForIds(allowedIds) : modulesForRole(user?.role);
  const orderIndex = (n) => { const i = prefs.order.indexOf(n); return i === -1 ? 500 + n : i; };
  const displayModules = roleModules
    .filter((m) => !prefs.hidden.includes(m.n))
    .slice()
    .sort((a, b) => {
      const pa = prefs.pinned.includes(a.n) ? 0 : 1;
      const pb = prefs.pinned.includes(b.n) ? 0 : 1;
      if (pa !== pb) return pa - pb;
      return orderIndex(a.n) - orderIndex(b.n);
    });
  const firstName = (user?.name || "").split(" ")[0];

  const loadBriefing = async () => {
    setBriefingLoading(true);
    try { const { data } = await api.get("/ai/briefing"); setBriefing(data.briefing); }
    catch (e) { setBriefing("AI briefing is temporarily unavailable. Your data is still shown below."); }
    finally { setBriefingLoading(false); }
  };

  useEffect(() => {
    api.get("/command/overview").then((r) => setData(r.data)).catch(() => {});
    api.get("/preferences").then((r) => setPrefs(r.data)).catch(() => {});
    api.get("/access/me").then((r) => setAllowedIds(r.data.module_ids)).catch(() => {});
    loadBriefing();
  }, []);

  useEffect(() => {
    if (isAdmin) api.get("/admin/access-log/anomalies").then((r) => setSecurity(r.data)).catch(() => {});
  }, [isAdmin]);

  const savePrefs = async (next) => { setPrefs(next); try { await api.put("/preferences", next); } catch (e) { toast.error("Could not save preferences"); } };
  const toggleHidden = (n) => { const hidden = prefs.hidden.includes(n) ? prefs.hidden.filter((x) => x !== n) : [...prefs.hidden, n]; savePrefs({ ...prefs, hidden }); };
  const togglePinned = (n) => { const pinned = prefs.pinned.includes(n) ? prefs.pinned.filter((x) => x !== n) : [...prefs.pinned, n]; savePrefs({ ...prefs, pinned }); };
  const toggleOverviewPref = () => savePrefs({ ...prefs, show_overview: !prefs.show_overview });

  const handleDrop = (targetN) => {
    if (dragItem == null || dragItem === targetN) { setDragItem(null); setDragOver(null); return; }
    const ids = displayModules.map((m) => m.n);
    const from = ids.indexOf(dragItem);
    const to = ids.indexOf(targetN);
    if (from === -1 || to === -1) { setDragItem(null); setDragOver(null); return; }
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    savePrefs({ ...prefs, order: ids });
    setDragItem(null); setDragOver(null);
    toast.success("Layout updated");
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{greeting()}, {firstName}!</h1>
          <p className="mt-1 text-sm text-muted-foreground">Welcome to your CAREIQ Productivity Suite. Select a product to get started.</p>
        </div>
        <button data-testid="customize-dashboard-btn" onClick={() => setCustomizeOpen(true)}
          className="flex items-center gap-2 rounded-md border bg-card px-3.5 py-2 text-sm font-medium transition-colors hover:border-[#155EEF] hover:text-[#155EEF]">
          <SlidersHorizontal size={15} /> Customize Dashboard
        </button>
      </div>

      {/* Module launcher grid */}
      <div className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground"><GripVertical size={13} /> Drag cards to arrange your workspace.</div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {displayModules.map((m, i) => {
          const Icon = m.icon;
          const pinned = prefs.pinned.includes(m.n);
          return (
            <div key={m.n} data-testid={`module-card-${m.n}`}
              draggable
              onDragStart={() => setDragItem(m.n)}
              onDragEnter={() => setDragOver(m.n)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(m.n)}
              onDragEnd={() => { setDragItem(null); setDragOver(null); }}
              className={cn(
                "careiq-rise group relative flex cursor-grab flex-col items-center rounded-xl border bg-card p-5 text-center shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:border-[color:var(--ring)] active:cursor-grabbing",
                dragItem === m.n && "opacity-40",
                dragOver === m.n && dragItem !== m.n && "ring-2 ring-[#155EEF] ring-offset-2 ring-offset-background"
              )}
              style={{ animationDelay: `${i * 45}ms` }}>
              <span className="absolute left-3 top-3 flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white shadow" style={{ backgroundColor: m.color }}>{m.n}</span>
              <GripVertical size={15} className="absolute right-3 top-3 text-muted-foreground/40 transition-colors group-hover:text-muted-foreground" data-testid={`drag-handle-${m.n}`} />
              {pinned && <Star size={13} className="absolute right-9 top-3.5 fill-[#F79009] text-[#F79009]" />}
              <span className="mb-3 flex h-16 w-16 items-center justify-center rounded-2xl transition-transform duration-200 group-hover:scale-110"
                style={{ backgroundColor: `${m.color}18`, color: m.color }}>
                <Icon size={30} strokeWidth={1.9} />
              </span>
              <h3 className="text-sm font-bold leading-snug" style={{ color: m.color }}>{m.title}</h3>
              <p className="mt-1.5 flex-1 text-xs leading-relaxed text-muted-foreground">{m.desc}</p>
              <button data-testid={`launch-${m.n}`} onClick={() => { recordRecentModule(m.n); navigate(m.to); }}
                className="mt-4 flex items-center gap-1.5 rounded-md border px-4 py-1.5 text-xs font-semibold transition-colors"
                style={{ color: m.color, borderColor: `${m.color}55` }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = m.color; e.currentTarget.style.color = "#fff"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = m.color; }}>
                Launch <ArrowRight size={13} />
              </button>
            </div>
          );
        })}
      </div>

      {(roleModules.length < MODULES.length || prefs.hidden.length > 0) && (
        <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Lock size={12} /> Showing {displayModules.length} of {roleModules.length} products available to your role ({roleLabel(user?.role)}){prefs.hidden.length > 0 ? ` · ${prefs.hidden.length} hidden` : ""}.
        </div>
      )}

      {/* AI Assistant banner */}
      {isAdmin && security && <SecurityWidget security={security} navigate={navigate} />}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-4 overflow-hidden rounded-xl border p-4 shadow-sm"
        style={{ background: "linear-gradient(90deg,#7C3AED,#155EEF)" }}>
        <div className="flex items-center gap-3 text-white">
          <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-white/20 backdrop-blur"><Sparkles size={22} /></span>
          <div>
            <div className="text-sm font-bold">CAREIQ AI Assistant</div>
            <div className="text-xs text-white/80">Ask questions, get insights, and automate workflows across the entire platform.</div>
          </div>
        </div>
        <button data-testid="open-ai-assistant-btn" onClick={() => ctx?.openCopilot?.()}
          className="flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-[#7C3AED] transition-transform hover:scale-105">
          Open AI Assistant <Sparkles size={15} />
        </button>
      </div>

      {/* Organization overview (toggleable) */}
      {prefs.show_overview && data && (
        <div className="mt-8">
          <div className="mb-4 flex items-center gap-2">
            <h2 className="text-lg font-bold tracking-tight">Organization Overview</h2>
            <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">Beacon Human Services</span>
          </div>
          <Overview data={data} briefing={briefing} briefingLoading={briefingLoading} loadBriefing={loadBriefing} navigate={navigate} />
        </div>
      )}
      {prefs.show_overview && !data && <div className="mt-8 flex h-40 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>}

      {customizeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setCustomizeOpen(false)} />
          <div className="careiq-scroll relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border bg-card p-6 shadow-2xl">
            <div className="mb-1 flex items-center justify-between">
              <h3 className="text-lg font-bold">Customize Dashboard</h3>
              <button data-testid="customize-close-btn" onClick={() => setCustomizeOpen(false)} className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
            </div>
            <p className="mb-4 text-xs text-muted-foreground">Pin favourites to the top, hide products you don't use, and toggle the organization overview.</p>

            <label className="mb-4 flex cursor-pointer items-center justify-between rounded-lg border bg-background p-3">
              <span className="text-sm font-medium">Show Organization Overview</span>
              <button data-testid="toggle-overview-pref" onClick={toggleOverviewPref}
                className={cn("relative h-6 w-11 rounded-full transition-colors", prefs.show_overview ? "bg-[#155EEF]" : "bg-muted")}>
                <span className={cn("absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform", prefs.show_overview ? "translate-x-5" : "translate-x-0.5")} />
              </button>
            </label>

            <div className="overline mb-2">Products ({roleModules.length} available)</div>
            <div className="space-y-2">
              {roleModules.map((m) => {
                const Icon = m.icon; const hidden = prefs.hidden.includes(m.n); const pinned = prefs.pinned.includes(m.n);
                return (
                  <div key={m.n} data-testid={`customize-row-${m.n}`} className={cn("flex items-center gap-3 rounded-lg border p-2.5 transition-opacity", hidden && "opacity-50")}>
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ backgroundColor: `${m.color}18`, color: m.color }}><Icon size={16} /></span>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{m.title}</span>
                    <button data-testid={`pin-${m.n}`} onClick={() => togglePinned(m.n)} title="Pin to top"
                      className={cn("flex h-8 w-8 items-center justify-center rounded-md border transition-colors", pinned ? "border-[#F79009] text-[#F79009]" : "text-muted-foreground hover:text-foreground")}>
                      <Star size={15} className={pinned ? "fill-[#F79009]" : ""} />
                    </button>
                    <button data-testid={`hide-${m.n}`} onClick={() => toggleHidden(m.n)} title={hidden ? "Show" : "Hide"}
                      className={cn("flex h-8 w-8 items-center justify-center rounded-md border transition-colors", hidden ? "text-muted-foreground" : "text-[#155EEF]")}>
                      {hidden ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                );
              })}
            </div>
            <button data-testid="customize-done-btn" onClick={() => { setCustomizeOpen(false); toast.success("Dashboard preferences saved"); }}
              className="mt-5 w-full rounded-md bg-[#155EEF] py-2.5 text-sm font-semibold text-white hover:bg-[#0F4FD1]">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

function SecurityWidget({ security, navigate }) {
  const alert = security.alert;
  const col = alert ? "#F04438" : "#12B76A";
  const Icon = alert ? ShieldAlert : ShieldCheck;
  return (
    <div data-testid="security-widget" className="mt-6 rounded-xl border p-4 shadow-sm"
      style={{ borderColor: `${col}55`, background: `${col}0d` }}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: `${col}1f`, color: col }}>
            <Icon size={20} />
          </span>
          <div>
            <div className="text-sm font-bold" style={{ color: col }}>
              {alert ? "Unusual sign-in activity detected" : "Sign-in activity normal"}
            </div>
            <div className="text-xs text-muted-foreground" data-testid="security-widget-summary">
              Last 24h: {security.failed_24h} failed sign-in{security.failed_24h === 1 ? "" : "s"} · {security.locked_24h} lockout{security.locked_24h === 1 ? "" : "s"}
              {security.suspicious_ips.length > 0 ? ` · ${security.suspicious_ips.length} suspicious IP${security.suspicious_ips.length === 1 ? "" : "s"}` : ""}
            </div>
          </div>
        </div>
        <button data-testid="security-widget-review-btn" onClick={() => navigate("/settings")}
          className="flex items-center gap-1.5 rounded-md border bg-card px-3.5 py-2 text-xs font-semibold transition-colors hover:border-[#155EEF] hover:text-[#155EEF]">
          Review in Security & Governance <ArrowRight size={13} />
        </button>
      </div>
      {alert && security.suspicious_ips.length > 0 && (
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {security.suspicious_ips.map((s) => (
            <div key={s.ip} data-testid={`suspicious-ip-${s.ip}`} className="rounded-lg border bg-card px-3 py-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="font-mono font-semibold">{s.ip}</span>
                <span className="rounded-full bg-[#F04438]/10 px-2 py-0.5 font-semibold text-[#F04438]">{s.failed} failed</span>
              </div>
              <div className="mt-0.5 truncate text-muted-foreground">{s.emails.join(", ")}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Overview({ data, briefing, briefingLoading, loadBriefing, navigate }) {
  const sc = data.scorecard || {};
  const c = data.counters || {};
  const kpis = [
    { icon: HeartPulse, label: "Org Health", value: sc.org_health, to: "/ai-insights" },
    { icon: AlertTriangle, label: "Resident Risk", value: sc.resident_risk, invert: true, to: "/health" },
    { icon: Users, label: "Staff Coverage", value: sc.staff_coverage, to: "/staffing" },
    { icon: ShieldCheck, label: "Compliance", value: sc.compliance_score, to: "/compliance" },
    { icon: Wallet, label: "Financial Health", value: sc.financial_health, to: "/finance" },
  ];
  const trend = (sc.trend || []).map((v, i) => ({ day: `D${i + 1}`, v }));
  return (
    <>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        {kpis.map((k, i) => {
          const col = k.invert ? scoreColor(100 - k.value) : scoreColor(k.value);
          return (
            <button key={k.label} data-testid={`kpi-${k.label.toLowerCase().replace(/ /g, "-")}`} onClick={() => navigate(k.to)}
              className="flex items-center gap-4 rounded-xl border bg-card p-4 text-left shadow-sm transition-colors hover:border-[#155EEF]">
              <ScoreRing value={k.value} color={col} />
              <div><div className="flex items-center gap-1.5 text-muted-foreground"><k.icon size={14} /><span className="overline">{k.label}</span></div>
                <div className="mt-0.5 text-xs text-muted-foreground">{k.invert ? "lower is better" : "of 100"}</div></div>
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-8 space-y-6">
          <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
            <div className="flex items-center justify-between border-b px-5 py-3.5" style={{ background: "linear-gradient(90deg,#7C3AED0d,transparent)" }}>
              <div className="flex items-center gap-2"><Sparkles size={16} className="text-[#7C3AED]" /><span className="text-sm font-bold tracking-tight">AI Executive Briefing</span></div>
              <button data-testid="regenerate-briefing-btn" onClick={loadBriefing} disabled={briefingLoading}
                className="flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50">
                <RefreshCw size={12} className={briefingLoading ? "animate-spin" : ""} /> Regenerate</button>
            </div>
            <div className="p-5">
              {briefingLoading ? <div className="space-y-2.5">{[...Array(5)].map((_, i) => <div key={i} className="h-3.5 rounded bg-muted" style={{ width: `${90 - i * 8}%` }} />)}</div>
                : <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-foreground/90">{briefing}</p>}
            </div>
          </div>
          <div className="rounded-xl border bg-card p-5 shadow-sm">
            <div className="mb-3 flex items-center justify-between"><span className="text-sm font-bold tracking-tight">Organizational Health Trend</span><span className="text-xs text-muted-foreground">last 7 days</span></div>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={trend} margin={{ left: 0, right: 0, top: 5, bottom: 0 }}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#155EEF" stopOpacity={0.35} /><stop offset="100%" stopColor="#155EEF" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }} />
                <Area type="monotone" dataKey="v" stroke="#155EEF" strokeWidth={2.5} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[{ label: "High-Risk Residents", value: c.high_risk_residents, to: "/health", col: "#F79009" },
              { label: "Missed/Refused Meds", value: c.missed_meds, to: "/medications", col: "#F04438" },
              { label: "Open Shifts", value: c.open_shifts, to: "/staffing", col: "#155EEF" },
              { label: "Overdue Compliance", value: c.overdue_compliance, to: "/compliance", col: "#F04438" }].map((s) => (
              <button key={s.label} onClick={() => navigate(s.to)} className="rounded-xl border bg-card p-4 text-left shadow-sm transition-colors hover:border-[#155EEF]">
                <div className="text-2xl font-bold tracking-tight" style={{ color: s.col }}>{s.value}</div><div className="mt-1 overline">{s.label}</div></button>
            ))}
          </div>
        </div>
        <div className="space-y-6 lg:col-span-4">
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="flex items-center justify-between border-b px-4 py-3"><div className="flex items-center gap-2 text-sm font-bold"><Bell size={15} className="text-[#F04438]" /> Active Alerts</div>
              <button onClick={() => navigate("/alerts")} className="text-xs font-medium text-[#155EEF] hover:underline">View all</button></div>
            <div className="careiq-scroll max-h-72 divide-y overflow-y-auto">
              {(data.alerts || []).slice(0, 6).map((a) => (
                <button key={a.id} onClick={() => navigate(a.link)} className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50">
                  <span className="mt-0.5"><PriorityBadge level={a.priority} /></span>
                  <span className="min-w-0 flex-1"><span className="block truncate text-[13px] font-medium">{a.title}</span><span className="block truncate text-xs text-muted-foreground">{a.detail}</span></span>
                  <ArrowRight size={14} className="mt-1 shrink-0 text-muted-foreground" /></button>
              ))}
            </div>
          </div>
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="flex items-center justify-between border-b px-4 py-3"><div className="flex items-center gap-2 text-sm font-bold"><CheckSquare size={15} className="text-[#155EEF]" /> My Tasks</div>
              <button onClick={() => navigate("/tasks")} className="text-xs font-medium text-[#155EEF] hover:underline">View all</button></div>
            <div className="careiq-scroll max-h-64 divide-y overflow-y-auto">
              {(data.tasks || []).slice(0, 6).map((t) => (
                <div key={t.id} className="flex items-center gap-3 px-4 py-2.5"><PriorityBadge level={t.priority} /><span className="min-w-0 flex-1 truncate text-[13px]">{t.title}</span><span className="shrink-0 text-[11px] text-muted-foreground">{t.due_date?.slice(5)}</span></div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <div className="mb-3 flex items-center gap-2"><Sparkles size={17} className="text-[#7C3AED]" /><h2 className="text-lg font-bold tracking-tight">Cross-Module AI Insights</h2></div>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">{(data.insights || []).map((ins, i) => <InsightCard key={ins.id} insight={ins} index={i} />)}</div>
      </div>
    </>
  );
}
